import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Collapse } from '@mui/material';
import classNames from 'classnames';
import { MenuItems } from './menuItems';  // Import MenuItems and MenuItem type
import HLDLogo from '../assets/images/DataViewLogo.png';
import FooterLogoSmall from '../assets/images/SidebarLogoFooterSmall.svg';
import { useLogin } from '../hooks/useLogin';
import { CollapsIcon } from '../assets/icons';

interface SubMenuItem {
  id: number;
  label: string;
  link: string;
}

interface MenuItem {
  id: number;
  label: string;
  icon: React.ReactElement;
  activeIcon: React.ReactElement;
  link: string;
  hasSubmenu?: boolean;
  subMenu?: SubMenuItem[];
}

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  const [activeSubmenuItem, setActiveSubmenuItem] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const { handleAuthenticationFailure } = useLogin();

  useEffect(() => {
    void handleAuthenticationFailure();
  }, [handleAuthenticationFailure]);
 
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    const handleResize = (): void => {
      const isSmallScreen = window.innerWidth < 1200;
      setIsMobile(isSmallScreen);
      setIsCollapsed(isSmallScreen);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const activeSubItem = MenuItems.flatMap((item) => item.subMenu ?? []).find(
      (subItem) => pathname.includes(subItem.link.toLowerCase())
    );

    if (activeSubItem) {
      setActiveSubmenuItem(activeSubItem.link);
      const parentMenu = MenuItems.find((item) =>
        item.subMenu?.some((sub) => sub.link === activeSubItem.link)
      );
      if (parentMenu) {
        setOpenSubmenu(parentMenu.id);
      }
    } else {
      const parentMenu = MenuItems.find((item) =>
        pathname.includes(item.link.toLowerCase())
      );
      if (parentMenu) {
        setOpenSubmenu(parentMenu.id);
        setActiveSubmenuItem(null);
      } else {
        setOpenSubmenu(null);
        setActiveSubmenuItem(null);
      }
    }
  }, [pathname]);

  const handleSidebarToggle = useCallback((): void => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleSubmenuToggle = useCallback((id: number): void => {
    setOpenSubmenu((prev) => (prev === id ? null : id));
    setIsCollapsed(false); // Always expand sidebar when submenu is clicked
  }, []);

  const renderArrowIcon = useCallback(
    (isOpen: boolean): JSX.Element => (
      <svg
        className={classNames('ml-auto transition-transform duration-200', {
          'transform rotate-180': isOpen,
        })}
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.33325 4.66675L5.99992 8.33341L9.66659 4.66675"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    []
  );

  const renderMenuItem = useCallback(
    (menu: MenuItem) => {
      const isActive = pathname.includes(menu.link.toLowerCase());
  
      const iconStyle = {
        width: '18px',
        height: '18px',
        marginTop: '-5px',  // Move icon up slightly
        marginLeft: isCollapsed ? '0px' : '-8px', // Adjust margin when collapsed
        marginRight: isCollapsed ? '10px' : '5px', // Increase space between icons when collapsed
      };
  
      return (
        <li key={menu.id} className="py-2">
          {menu.hasSubmenu === false ? (
            <Link
              to={menu.link}
              className={classNames('menu-link py-2 flex items-center', {
                active: isActive,
              })}
              onClick={() => setIsCollapsed(false)}
            >
              {/* Properly align icon and label */}
              <div className="flex items-center gap-2">
                <div style={iconStyle}>
                  {isActive ? menu.activeIcon : menu.icon}
                </div>
                {!isCollapsed && <span>{menu.label}</span>}
              </div>
            </Link>
          ) : (
            <>
              <div
                className={classNames('menu-link py-2 cursor-pointer flex items-center justify-between', {
                  active: isActive,
                })}
                onClick={() => handleSubmenuToggle(menu.id)}
              >
                <div className="flex items-center gap-2">
                  <div style={iconStyle}>
                    {isActive ? menu.activeIcon : menu.icon}
                  </div>
                  {!isCollapsed && <span>{menu.label}</span>}
                </div>
                {!isCollapsed && renderArrowIcon(openSubmenu === menu.id)}
              </div>
              {!isCollapsed && (
                <Collapse in={openSubmenu === menu.id} timeout="auto" unmountOnExit>
                  <ul className="pl-4 mt-2 submenu">
                    {menu?.subMenu?.map((subItem) => (
                      <li key={subItem.id} className="pt-1 pb-1 flex submenu-item">
                        <Link
                          to={subItem.link}
                          className={classNames('menu-link py-1 flex items-center', {
                            active: activeSubmenuItem === subItem.link,
                          })}
                          onClick={() => setIsCollapsed(false)}
                        >
                          <span className="menu-link-text">{subItem.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Collapse>
              )}
            </>
          )}
        </li>
      );
    },
    [pathname, activeSubmenuItem, openSubmenu, isCollapsed, handleSubmenuToggle, renderArrowIcon]
  );
  
  

  const wrapperClasses = classNames(
    "bg-white flex justify-between flex-col",
    {
      ["max-w-[230px]"]: !isCollapsed && !isMobile,
      ["min-w-[80px]"]: isCollapsed || isMobile,
    }
  );

  const collapseIconClasses = classNames(
    'rounded bg-secondary absolute right-0 ',
    {
      'rotate-180 collapsed': isCollapsed,
    }
  );

  return (
    <div className={wrapperClasses} style={{ transition: 'width 300ms cubic-bezier(0.2, 0, 0, 1)' }}>
      <div className="flex flex-col">
        <div className="flex items-center justify-between relative h-[60px]">
          <div className="flex items-center gap-4" style={{ margin: 'auto', padding: '10px' }}>
            <img
              src={isCollapsed ? FooterLogoSmall : HLDLogo}
              alt="logo"
              width={isCollapsed ? 50 : 120}
              height={66}
              className={`mx-auto ${isCollapsed ? 'w-[30px] h-[40px]' : 'w-[180px] h-[66px]'}`}
            />
          </div>
          <button className={collapseIconClasses} onClick={handleSidebarToggle} aria-label="Toggle sidebar">
            <CollapsIcon />
          </button>
        </div>

        <div className="flex flex-col items-center mt-5 px-4 overflow-y-auto overflow-x-hidden">
          <ul className={classNames('menu sidebar-menu-container', { 'w-60': !isCollapsed })}>
            {MenuItems.map(renderMenuItem)}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
